import { AppNode } from "@features/builder/graph/types"
import { getParentNodes } from "@features/builder/helpers/nodes"
import { useGetLinkedNode } from "@features/builder/hooks/useGetLinkedNode"
import { TKNode } from "@store/services/converter/types"
import { useNodes } from "@xyflow/react"
import { useEffect, useMemo, useState } from "react"

/**
 * This hook retrieves the linked nodes of the parents of a given node.
 * It returns a map where the keys are the parent node IDs and the values are the corresponding linked nodes.
 *
 * @param node - The node for which to retrieve the parents' linked nodes.
 * @returns A map of parent node IDs to their linked nodes.
 */
export const useNodesParentsLinkedNodes = (node: AppNode | undefined) => {
  const [linksMap, setLinksMap] = useState<Map<string, TKNode>>(new Map())
  const allNodes = useNodes<AppNode>()
  const { getLinkedNode } = useGetLinkedNode()

  const parentsNodes = useMemo(() => {
    if (!node) return []

    return getParentNodes(node, allNodes)
  }, [node, allNodes])

  useEffect(() => {
    const allLinks = Promise.all(
      parentsNodes.map(async (parentNode) => {
        const linkId = "metaNode" in parentNode.data ? parentNode.data.metaNode?.Link : undefined
        const linkedNode = await getLinkedNode(linkId)

        return { parentId: parentNode.id, linkedNode }
      }),
    )

    allLinks.then((links) => {
      const linksMap = new Map<string, TKNode>()
      links.forEach(({ parentId, linkedNode }) => {
        if (!linkedNode) return
        linksMap.set(parentId, linkedNode)
      })
      setLinksMap(linksMap)
    })
  }, [parentsNodes, getLinkedNode])

  return linksMap
}
